export type ThemeId = 'bloomberg-amber' | 'cyberpunk-neon' | 'wsb-chaos' | 'night-trader' | 'mono-stark' | 'doomer-grey' | 'gold-tycoon';

export interface ThemeMeta {
  id: ThemeId;
  label: string;
  unlockCondition?: { highestTierRank?: number; ascensions?: number };
}

export const THEME_META: ThemeMeta[] = [
  { id: 'bloomberg-amber', label: 'Bloomberg amber' },
  { id: 'cyberpunk-neon', label: 'Cyberpunk neon', unlockCondition: { highestTierRank: 2 } }, // unlock at Whale
  { id: 'night-trader', label: 'Night trader', unlockCondition: { ascensions: 1 } },
  { id: 'wsb-chaos', label: 'r/wsb chaos', unlockCondition: { ascensions: 2 } },
  { id: 'mono-stark', label: 'Stark monochrome' },          // always available — just black-on-white
  { id: 'doomer-grey', label: 'Doomer grey', unlockCondition: { highestTierRank: 1 } },
  { id: 'gold-tycoon', label: 'Gold tycoon', unlockCondition: { highestTierRank: 3 } }, // unlock at Tycoon
];

const BLOOMBERG_AMBER: Record<string, string> = {
  '--bg-deep': '#060606',
  '--bg-surface': '#0a0a0a',
  '--bg-elevated': '#141414',
  '--border-subtle': '#1f1f1f',
  '--border-strong': '#3a2c00',
  '--text-primary': '#e0a83b',
  '--text-secondary': '#a87a25',
  '--text-muted': '#5e4515',
  '--accent-primary': '#ffb84d',
  '--positive': '#4ec9b0',
  '--negative': '#f06b6b',
  '--warning': '#d6a85c',
  '--critical': '#ff3030',
};

const CYBERPUNK_NEON: Record<string, string> = {
  '--bg-deep': '#06031a',
  '--bg-surface': '#0c0628',
  '--bg-elevated': '#170a3a',
  '--border-subtle': '#1f1247',
  '--border-strong': '#3a1474',
  '--text-primary': '#ff31e0',
  '--text-secondary': '#b522a0',
  '--text-muted': '#5e1156',
  '--accent-primary': '#00f0ff',
  '--positive': '#0bff7a',
  '--negative': '#ff3070',
  '--warning': '#ffd233',
  '--critical': '#ff0044',
};

const NIGHT_TRADER: Record<string, string> = {
  '--bg-deep': '#0a1224',
  '--bg-surface': '#101b35',
  '--bg-elevated': '#16264a',
  '--border-subtle': '#1d2e58',
  '--border-strong': '#2a4080',
  '--text-primary': '#9bbef5',
  '--text-secondary': '#6f8fc8',
  '--text-muted': '#43577f',
  '--accent-primary': '#5fa7ff',
  '--positive': '#5be3a7',
  '--negative': '#ff7088',
  '--warning': '#ffb74d',
  '--critical': '#ff4858',
};

const WSB_CHAOS: Record<string, string> = {
  '--bg-deep': '#000000',
  '--bg-surface': '#0a0a0a',
  '--bg-elevated': '#141414',
  '--border-subtle': '#1a1a1a',
  '--border-strong': '#3a3a3a',
  '--text-primary': '#39ff14',
  '--text-secondary': '#22a805',
  '--text-muted': '#0e6504',
  '--accent-primary': '#ff14b3',
  '--positive': '#39ff14',
  '--negative': '#ff1414',
  '--warning': '#ffea00',
  '--critical': '#ff0040',
};

const MONO_STARK: Record<string, string> = {
  '--bg-deep': '#000000',
  '--bg-surface': '#0c0c0c',
  '--bg-elevated': '#1a1a1a',
  '--border-subtle': '#262626',
  '--border-strong': '#444444',
  '--text-primary': '#f0f0f0',
  '--text-secondary': '#a0a0a0',
  '--text-muted': '#606060',
  '--accent-primary': '#ffffff',
  '--positive': '#a0e0a0',
  '--negative': '#e0a0a0',
  '--warning': '#e0c080',
  '--critical': '#ff8080',
};

const DOOMER_GREY: Record<string, string> = {
  '--bg-deep': '#0d0e10',
  '--bg-surface': '#15171a',
  '--bg-elevated': '#1f2226',
  '--border-subtle': '#2a2e34',
  '--border-strong': '#454a52',
  '--text-primary': '#9ca3ad',
  '--text-secondary': '#6b727a',
  '--text-muted': '#444a53',
  '--accent-primary': '#7d8590',
  '--positive': '#5a8b73',
  '--negative': '#8b5a5a',
  '--warning': '#8b7d5a',
  '--critical': '#a04545',
};

const GOLD_TYCOON: Record<string, string> = {
  '--bg-deep': '#1a0e00',
  '--bg-surface': '#26170a',
  '--bg-elevated': '#3a2615',
  '--border-subtle': '#4a3120',
  '--border-strong': '#7a5535',
  '--text-primary': '#f5d97c',
  '--text-secondary': '#c4a04f',
  '--text-muted': '#7a6232',
  '--accent-primary': '#ffd400',
  '--positive': '#7ae3a0',
  '--negative': '#e8836b',
  '--warning': '#ffaa00',
  '--critical': '#ff3b3b',
};

export const THEME_VARS: Record<ThemeId, Record<string, string>> = {
  'bloomberg-amber': BLOOMBERG_AMBER,
  'cyberpunk-neon': CYBERPUNK_NEON,
  'night-trader': NIGHT_TRADER,
  'wsb-chaos': WSB_CHAOS,
  'mono-stark': MONO_STARK,
  'doomer-grey': DOOMER_GREY,
  'gold-tycoon': GOLD_TYCOON,
};

export function applyTheme(id: ThemeId): void {
  const vars = THEME_VARS[id] ?? THEME_VARS['bloomberg-amber'];
  for (const [k, v] of Object.entries(vars)) {
    document.documentElement.style.setProperty(k, v);
  }
}
